import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'


/** API */
import AppointmentDurationApi from 'api/setting/defaults/appointment-duration.api'

/** UTILS || SLICE */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { convertDurationToMinuteOrText } from 'utils/date.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const initStateErrorMessage = {
  APPOINTMENT_DURATION: ``,
  IS_DUPLICATE_APPOINTMENT_DURATION: ``,
  APPOINTMENT_DURATION_STRING_BASE: ``,
  APPOINTMENT_DURATION_STRING_EMPTY: ``,
  APPOINTMENT_DURATION_MAXIMUM_LENGTH: ``,
  APPOINTMENT_DURATION_ANY_REQUIRED: ``
}
const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
export default function AppointmentDurations() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [appointmentDurations, setAppointmentDurations] = useState([])

  const [appointmentDurationId, setAppointmentDurationId] = useState(Number)
  const [appointmentDuration, setAppointmentDuration] = useState('5')
  const [appointmentDurationType, setAppointmentDurationType] = useState<any>('MINUTES')

  const [appointmentDurationStatus, setAppointmentDurationStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)



  const loadData = async () => {
    try {
      const resp = await AppointmentDurationApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setAppointmentDurations(resp.data)
      } else {
        setAppointmentDurations([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])



  const submit = () => {
    if (!appointmentDuration) return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_STRING_EMPTY') } })
    if ((appointmentDurationType === 'MINUTES') && Number(appointmentDuration) < 5) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES', { unit: `5 ${t(`APPOINTMENT_DURATION.MESSAGE.${appointmentDurationType}`)}` }) } })
    } else if ((appointmentDurationType === 'HOURS' || appointmentDurationType === 'DAY') && Number(appointmentDuration) < 1) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES', { unit: `1 ${t(`APPOINTMENT_DURATION.MESSAGE.${appointmentDurationType}`)}` }) } })
    }
    dispatch(submitModal())
    setDisabledSubmit(true)

    const durationMinute = convertDurationToMinuteOrText(appointmentDuration, appointmentDurationType)
    AppointmentDurationApi.create({ duration: appointmentDuration, durationType: appointmentDurationType, durationMinute })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!appointmentDuration) return setErrorMessage({ ...errorMessage, ...{ DURATION: t(`APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_STRING_EMPTY`) } })
    if ((appointmentDurationType === 'MINUTES') && Number(appointmentDuration) < 5) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES', { unit: `5 ${t(`APPOINTMENT_DURATION.MESSAGE.${appointmentDurationType}`)}` }) } })
    } else if ((appointmentDurationType === 'HOURS' || appointmentDurationType === 'DAY') && Number(appointmentDuration) < 1) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES', { unit: `1 ${t(`APPOINTMENT_DURATION.MESSAGE.${appointmentDurationType}`)}` }) } })
    }

    dispatch(submitModal())
    setDisabledSubmit(true)
    const durationMinute = convertDurationToMinuteOrText(appointmentDuration, appointmentDurationType)
    AppointmentDurationApi.update(appointmentDurationId, {
      duration: appointmentDuration,
      durationType: appointmentDurationType,
      durationMinute: durationMinute,
      status: appointmentDurationStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`APPOINTMENT_DURATION.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          if (err.message === 'IS_DUPLICATE_APPOINTMENT_DURATION') {

            setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
          } else setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setAppointmentDurationId(0)
    setAppointmentDuration('5')
    setAppointmentDurationType('MINUTES')
    setAppointmentDurationStatus('')
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      AppointmentDurationApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      AppointmentDurationApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { appointmentDurationId: aptDurationId, duration: aptDuration, durationType: aptDurationType, status: aptDurationStatus } = data
    setAppointmentDurationId(aptDurationId)
    setAppointmentDuration(aptDuration)
    setAppointmentDurationType(aptDurationType)
    setAppointmentDurationStatus(aptDurationStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const handleChangeDurationType = (type: string) => {
    setAppointmentDurationType(type)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.DURATION') },
    { id: 'status', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentDurationId: aptDurationId, duration: aptDuration, durationType: aptDurationType, updatedBy, updatedAt, status: aptDurationStatus } = objData
    const statusBtnActive = aptDurationStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = aptDurationStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: aptDurationId,
      id: aptDurationId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: t(`APPOINTMENT_DURATION.TABLE.CELL.${aptDurationType}`, { duration: aptDuration }) },
        { option: 'STATUS', align: 'center', label: aptDurationStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }
  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(t(`APPOINTMENT_DURATION.TABLE.CELL.${obj.durationType}`, { duration: obj.duration }))
  }
  return (
    <Box >
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { appointmentDuration: name, status: t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('APPOINTMENT_DURATION.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('APPOINTMENT_DURATION.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />
              <MainButton
                title={t('APPOINTMENT_DURATION.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsPopup(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={appointmentDurations.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          title={appointmentDurationId ? t('APPOINTMENT_DURATION.TITLE_UPDATE') : t('APPOINTMENT_DURATION.TITLE_CREATE')}
          isOpen={isPopup}
          onCancel={{ onClick: () => resetForm() }}
          onSubmit={{ onClick: () => appointmentDurationId ? submitEdit() : submit(), disabled: disabledSubmit }}
          handleClose={resetForm}
          size={'XS'}
        >
          <Box py={2}>
            {appointmentDurationId ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                <SwitchButton
                  onChange={() => setAppointmentDurationStatus(appointmentDurationStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  checked={appointmentDurationStatus === 'ACTIVE' ? true : false}
                />
              </Box>
            ) : (
              ''
            )}
            <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
              <Box>
                <TextInput
                  fullWidth
                  onChange={(event) => {
                    setAppointmentDuration(String(Number(event.target.value) || 0))
                    clearErrorMessage()
                  }}
                  value={appointmentDuration}
                  label={t('APPOINTMENT_DURATION.INPUT.DURATION')}
                  helperText={
                    errorMessage.APPOINTMENT_DURATION ||
                    errorMessage.IS_DUPLICATE_APPOINTMENT_DURATION ||
                    errorMessage.APPOINTMENT_DURATION_STRING_BASE ||
                    errorMessage.APPOINTMENT_DURATION_STRING_EMPTY ||
                    errorMessage.APPOINTMENT_DURATION_MAXIMUM_LENGTH ||
                    errorMessage.APPOINTMENT_DURATION_ANY_REQUIRED
                  }
                  required={true}
                />
              </Box>
              <Box>
                <SelectInput
                  onChange={(event) => {
                    const value = event.target.value
                    if (value) {
                      handleChangeDurationType(value)
                      clearErrorMessage()
                    }
                  }}
                  label={t('APPOINTMENT_DURATION.INPUT.DURATION_TYPE')}
                  value={appointmentDurationType}
                  options={[{ value: "MINUTES", name: t('TIME_DURATION_TYPE.MINUTES') },
                  { value: "HOURS", name: t('TIME_DURATION_TYPE.HOURS') },
                  { value: "DAY", name: t('TIME_DURATION_TYPE.DAY') },]}
                />
              </Box>
            </Box>
          </Box>
        </PageModal>
      </Box>
    </Box >
  )
}
